if (!customElements.get('cinoton-color-showcase')) {
  customElements.define('cinoton-color-showcase', class extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.tabs = Array.from(this.querySelectorAll('[data-cinoton-color-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-cinoton-color-panel]'));
      this.currentLabel = this.querySelector('[data-cinoton-color-current]');
      this.index = Math.max(0, this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true'));
      this.transitionToken = 0;
      this.animations = [];
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.panels.forEach((panel, index) => {
        const selected = index === this.index;
        panel.hidden = !selected;
        panel.setAttribute('aria-hidden', String(!selected));
      });

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.activate(index));
        tab.addEventListener('keydown', (event) => this.handleKeydown(event, index));
      });

      this.querySelector('[data-cinoton-color-previous]')?.addEventListener('click', () => this.activate(this.index - 1, false, -1));
      this.querySelector('[data-cinoton-color-next]')?.addEventListener('click', () => this.activate(this.index + 1, false, 1));
      this.addEventListener('shopify:block:select', (event) => {
        const panel = event.target.closest('[data-cinoton-color-panel]');
        const index = this.panels.indexOf(panel);
        if (index >= 0) this.activate(index);
      });
    }

    shortestDirection(nextIndex) {
      const count = this.tabs.length;
      const forward = (nextIndex - this.index + count) % count;
      const backward = (this.index - nextIndex + count) % count;
      return forward <= backward ? 1 : -1;
    }

    finishTransition() {
      this.transitionToken += 1;
      this.animations.forEach((animation) => animation.cancel());
      this.animations = [];
      this.panels.forEach((panel, index) => {
        const selected = index === this.index;
        panel.hidden = !selected;
        panel.setAttribute('aria-hidden', String(!selected));
      });
    }

    activate(nextIndex, focus = false, requestedDirection = 0) {
      if (!this.tabs.length) return;
      const normalizedIndex = (nextIndex + this.tabs.length) % this.tabs.length;
      if (normalizedIndex === this.index) {
        if (focus) this.tabs[this.index].focus();
        return;
      }

      this.finishTransition();
      const previousIndex = this.index;
      const direction = requestedDirection || this.shortestDirection(normalizedIndex);
      const outgoing = this.panels[previousIndex];
      const incoming = this.panels[normalizedIndex];
      this.index = normalizedIndex;
      const token = this.transitionToken;

      this.tabs.forEach((tab, index) => {
        const selected = index === this.index;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });
      outgoing.setAttribute('aria-hidden', 'true');
      incoming.hidden = false;
      incoming.setAttribute('aria-hidden', 'false');

      if (this.currentLabel) this.currentLabel.textContent = this.tabs[this.index].dataset.cinotonColorLabel || '';
      if (focus) this.tabs[this.index].focus();

      if (this.reduceMotion || !outgoing || !incoming || typeof incoming.animate !== 'function') {
        this.panels.forEach((panel, index) => { panel.hidden = index !== this.index; });
        return;
      }

      const durationValue = parseFloat(getComputedStyle(this).getPropertyValue('--color-story-duration')) || 500;
      const timing = { duration: durationValue, easing: 'cubic-bezier(.22,.72,.18,1)', fill: 'both' };
      const outgoingAnimation = outgoing.animate([
        { transform: 'translate3d(0,0,0)', opacity: 1 },
        { transform: `translate3d(${-direction * 100}%,0,0)`, opacity: .94 }
      ], timing);
      const incomingAnimation = incoming.animate([
        { transform: `translate3d(${direction * 100}%,0,0)`, opacity: .94 },
        { transform: 'translate3d(0,0,0)', opacity: 1 }
      ], timing);
      this.animations = [outgoingAnimation, incomingAnimation];

      Promise.all(this.animations.map((animation) => animation.finished.catch(() => null))).then(() => {
        if (token !== this.transitionToken) return;
        this.animations = [];
        this.panels.forEach((panel, index) => {
          const selected = index === this.index;
          panel.hidden = !selected;
          panel.setAttribute('aria-hidden', String(!selected));
        });
      });
    }

    handleKeydown(event, index) {
      const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
      if (!keys.includes(event.key)) return;
      event.preventDefault();
      let nextIndex = index;
      let direction = 0;
      if (event.key === 'ArrowLeft') { nextIndex -= 1; direction = -1; }
      if (event.key === 'ArrowRight') { nextIndex += 1; direction = 1; }
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.tabs.length - 1;
      this.activate(nextIndex, true, direction);
    }
  });
}
